/**
 * LabelManager - Component for managing project labels (create, edit, delete)
 */

import { useState } from 'react';
import { clsx } from 'clsx';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Input } from '@components/ui/input';
import Badge from '@components/ui/Badge';
import { toast } from '@components/ui/Toast';
import { labelsApi, type Label } from '@services/api';
import { useTranslation } from 'react-i18next';

// Default color for labels
const DEFAULT_LABEL_COLOR = '#6B7280';

// Predefined color palette for labels
const LABEL_COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#EAB308', // yellow
  '#84CC16', // lime
  '#22C55E', // green
  '#14B8A6', // teal
  '#06B6D4', // cyan
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#A855F7', // purple
  '#D946EF', // fuchsia
  '#EC4899', // pink
  '#6B7280', // gray
];

interface LabelManagerProps {
  projectId: string;
  labels: Label[];
  onLabelsChange: (labels: Label[]) => void;
}

interface EditingLabel {
  id: string | null;
  name: string;
  color: string;
}

export default function LabelManager({
  projectId,
  labels,
  onLabelsChange,
}: LabelManagerProps) {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [editingLabel, setEditingLabel] = useState<EditingLabel | null>(null);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(LABEL_COLORS[0] ?? DEFAULT_LABEL_COLOR);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;

    setIsSubmitting(true);
    try {
      const newLabel = await labelsApi.create({
        projectId,
        name: newLabelName.trim(),
        color: newLabelColor,
      });
      onLabelsChange([...labels, newLabel]);
      setIsCreating(false);
      setNewLabelName('');
      setNewLabelColor(LABEL_COLORS[0] ?? DEFAULT_LABEL_COLOR);
      toast.success(t('task.labelCreated'));
    } catch (error) {
      console.error('Failed to create label:', error);
      toast.error(t('task.failedCreateLabel'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateLabel = async () => {
    if (!editingLabel || !editingLabel.id || !editingLabel.name.trim()) return;

    setIsSubmitting(true);
    try {
      const updatedLabel = await labelsApi.update(editingLabel.id, {
        name: editingLabel.name.trim(),
        color: editingLabel.color,
      });
      onLabelsChange(
        labels.map((l) => (l.id === editingLabel.id ? updatedLabel : l))
      );
      setEditingLabel(null);
      toast.success(t('task.labelUpdated'));
    } catch (error) {
      console.error('Failed to update label:', error);
      toast.error(t('task.failedUpdateLabel'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLabel = async (labelId: string) => {
    setDeletingId(labelId);
    try {
      await labelsApi.delete(labelId);
      onLabelsChange(labels.filter((l) => l.id !== labelId));
      toast.success(t('task.labelDeleted'));
    } catch (error) {
      console.error('Failed to delete label:', error);
      toast.error(t('task.failedDeleteLabel'));
    } finally {
      setDeletingId(null);
    }
  };

  const startEditing = (label: Label) => {
    setEditingLabel({
      id: label.id,
      name: label.name,
      color: label.color,
    });
    setIsCreating(false);
  };

  const cancelEditing = () => {
    setEditingLabel(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
          {t('task.projectLabels')}
        </h3>
        {!isCreating && !editingLabel && (
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            className={clsx(
              'flex items-center gap-1 rounded-md px-2 py-1 text-sm',
              'text-primary-600 hover:bg-primary-50',
              'dark:text-primary-400 dark:hover:bg-primary-900/20'
            )}
          >
            <Plus className="h-4 w-4" />
            {t('task.addLabel')}
          </button>
        )}
      </div>

      {/* Create new label form */}
      {isCreating && (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-800/50">
          <div className="space-y-3">
            <Input
              type="text"
              placeholder={t('task.labelNamePlaceholder')}
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCreateLabel();
                }
                if (e.key === 'Escape') {
                  setIsCreating(false);
                  setNewLabelName('');
                }
              }}
              className="h-9"
              autoFocus
            />
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5">
                {t('task.colorLabel')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {LABEL_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewLabelColor(color)}
                    className={clsx(
                      'h-6 w-6 rounded-full transition-all',
                      newLabelColor === color &&
                        'ring-2 ring-offset-2 ring-neutral-900 dark:ring-white dark:ring-offset-neutral-800'
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge color={newLabelColor} size="md">
                {newLabelName || 'Preview'}
              </Badge>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsCreating(false);
                  setNewLabelName('');
                }}
                className="flex-1 rounded-md px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-200 dark:text-neutral-400 dark:hover:bg-neutral-700"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleCreateLabel}
                disabled={!newLabelName.trim() || isSubmitting}
                className={clsx(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium',
                  'bg-primary-600 text-white hover:bg-primary-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSubmitting ? t('common.creating') : t('task.createLabel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Labels list */}
      <div className="space-y-1">
        {labels.length === 0 && !isCreating ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 py-4 text-center">
            {t('task.noLabelsYet')}
          </p>
        ) : (
          labels.map((label) =>
            editingLabel?.id === label.id ? (
              // Editing form
              <div
                key={label.id}
                className="rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20"
              >
                <div className="space-y-3">
                  <Input
                    type="text"
                    value={editingLabel.name}
                    onChange={(e) =>
                      setEditingLabel({ ...editingLabel, name: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleUpdateLabel();
                      }
                      if (e.key === 'Escape') {
                        cancelEditing();
                      }
                    }}
                    className="h-9"
                    autoFocus
                  />
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5">
                      {t('task.colorLabel')}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {LABEL_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() =>
                            setEditingLabel({ ...editingLabel, color })
                          }
                          className={clsx(
                            'h-6 w-6 rounded-full transition-all',
                            editingLabel.color === color &&
                              'ring-2 ring-offset-2 ring-neutral-900 dark:ring-white dark:ring-offset-neutral-800'
                          )}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge color={editingLabel.color} size="md">
                      {editingLabel.name || 'Preview'}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="flex-1 rounded-md px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-200 dark:text-neutral-400 dark:hover:bg-neutral-700"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleUpdateLabel}
                      disabled={!editingLabel.name.trim() || isSubmitting}
                      className={clsx(
                        'flex-1 rounded-md px-3 py-1.5 text-sm font-medium',
                        'bg-primary-600 text-white hover:bg-primary-700',
                        'disabled:cursor-not-allowed disabled:opacity-50'
                      )}
                    >
                      {isSubmitting ? t('common.saving') : t('common.save')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Label row
              <div
                key={label.id}
                className={clsx(
                  'flex items-center justify-between rounded-lg px-3 py-2',
                  'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                  'group transition-colors'
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: label.color }}
                  />
                  <span className="text-sm text-neutral-900 dark:text-white">
                    {label.name}
                  </span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => startEditing(label)}
                    className="rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
                    title={t('task.editLabel')}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteLabel(label.id)}
                    disabled={deletingId === label.id}
                    className={clsx(
                      'rounded p-1 text-neutral-500 hover:bg-red-100 hover:text-red-600',
                      'dark:hover:bg-red-900/30 dark:hover:text-red-400',
                      'disabled:cursor-not-allowed disabled:opacity-50'
                    )}
                    title={t('common.delete')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )
          )
        )}
      </div>
    </div>
  );
}
